import { useEffect, useState } from 'react';
import type {
  AnalysisResult,
  ChipPlan,
  PlayerAssessment,
  RankContext,
  RankLeagueStanding,
  SquadValidation,
  TransferPlan,
} from '@fantasy/types';
import { Link } from '@tanstack/react-router';
import { AnalysisView } from './AnalysisView.tsx';
import { ChipCalendar } from './ChipCalendar.tsx';
import { ChipStatus } from './ChipStatus.tsx';
import { DeadlineCountdown } from './DeadlineCountdown.tsx';
import { LinkFplTeam } from './LinkFplTeam.tsx';
import { TransferPlanView } from './TransferPlanView.tsx';
import { api, type LivePlayerPoints, type OfficialGameweekPoints } from '../api.ts';
import { estimateSquadPoints } from '../lib/estimateSquadPoints.ts';
import { resolveGameweekPoints } from '../lib/gameweekPoints.ts';
import { useGameweek } from '../lib/useGameweek.ts';
import { useSquad } from '../state/useSquad.ts';

/** The fitness flags worth surfacing as urgent before the deadline —
 * `rotation_risk`, `blank` and `double` are fixture-outlook concerns
 * `AnalysisView` already covers in depth once "Run the desk" has actually
 * run; these three are the ones that need attention right now, independent
 * of whether analysis has even been run this session. */
const URGENT_FLAGS = new Set<PlayerAssessment['flags'][number]>(['injured', 'doubtful', 'suspended']);

/**
 * The app's home surface (T-075) — the weekly rhythm the squad desk, the
 * fixture grid, the tracker and the rest never had one place to answer:
 * what should a manager do right now? Renders one of three layouts off
 * `useGameweek()`'s `phase`, degrading to the pre-deadline layout (with no
 * countdown) when that clock isn't known yet — an unknown phase must never
 * cost a manager the page. With no squad loaded, none of the three layouts
 * has anything to show, so this renders a way to get one instead of an
 * empty shell.
 */
export function ThisWeek() {
  const {
    squad, validation, analysis, plan, chipPlan,
    problems, planProblems, chipPlanProblems,
    phase, planning, chipPlanning, runTheDesk,
  } = useSquad();
  const meta = useGameweek();
  const gwPhase = meta?.phase ?? null;

  const [livePoints, setLivePoints] = useState<Map<number, LivePlayerPoints> | null>(null);
  const [official, setOfficial] = useState<OfficialGameweekPoints | null>(null);

  // Only the live/post layouts show a points figure — pre-deadline there's
  // nothing scored yet to fetch. Cleared on every squad or phase change so a
  // previous squad's (or gameweek's) total can't linger under a new one's,
  // same reasoning as SquadContextBar's own live-points fetch.
  useEffect(() => {
    setLivePoints(null);
    setOfficial(null);
    if (!squad || (gwPhase !== 'live' && gwPhase !== 'post')) return;
    let cancelled = false;
    api
      .getLivePoints(squad.id)
      .then(({ points, official: fetchedOfficial }) => {
        if (cancelled) return;
        setLivePoints(new Map(points.map((p) => [p.fplId, p])));
        setOfficial(fetchedOfficial ?? null);
      })
      .catch(() => {
        // Not fatal — same reasoning as SquadContextBar's own fetch: the
        // rest of the layout still renders, just without a points figure.
      });
    return () => {
      cancelled = true;
    };
  }, [squad?.id, gwPhase]);

  // Same resolver SquadContextBar uses (T-113) — the two must never disagree.
  const estimated = squad ? estimateSquadPoints(squad, livePoints) : null;
  const { points } = resolveGameweekPoints(official, estimated);

  // Where the manager actually stands (T-076) — read once per squad, not
  // per phase, since a rank doesn't change with the gameweek clock. `null`
  // for a squad with no FPL entry id (no fetch made at all — see the guard
  // below) and, just as importantly, for one where the fetch failed: every
  // desk layout must keep working with no standing to show, so a failure
  // here is swallowed exactly like the live-points fetch above.
  const [rank, setRank] = useState<RankContext | null>(null);
  const hasEntryId = squad?.entryId !== undefined;

  useEffect(() => {
    setRank(null);
    if (!squad || squad.entryId === undefined) return;
    let cancelled = false;
    api
      .getRankContext(squad.id)
      .then(({ rank: fetched }) => {
        if (!cancelled) setRank(fetched);
      })
      .catch(() => {
        // Not fatal — the standing is simply absent; every desk layout
        // below still renders without it.
      });
    return () => {
      cancelled = true;
    };
  }, [squad?.id, squad?.entryId]);

  return (
    <>
      <section className="hero" aria-labelledby="this-week-heading">
        <h2 id="this-week-heading">This Week</h2>
        <DeadlineCountdown />
      </section>

      {/* A manager who played a chip should see the app knows before they go
       * looking for it — this is the home surface, so it renders here too,
       * not just on the squad route. `LinkFplTeam` renders nothing of its
       * own for a squad that's already linked (or none at all). */}
      {squad && <ChipStatus squad={squad} />}
      {squad && <LinkFplTeam />}

      {!squad ? (
        <NoSquadPanel />
      ) : gwPhase === 'live' ? (
        <LiveDesk gameweek={squad.gameweek} points={points} rank={rank} hasEntryId={hasEntryId} />
      ) : gwPhase === 'post' ? (
        <PostDesk gameweek={squad.gameweek} points={points} rank={rank} hasEntryId={hasEntryId} />
      ) : (
        <PreDeadlineDesk
          validation={validation}
          analysis={analysis}
          plan={plan}
          chipPlan={chipPlan}
          problems={problems}
          planProblems={planProblems}
          chipPlanProblems={chipPlanProblems}
          running={phase === 'working' || planning || chipPlanning}
          onRunTheDesk={() => runTheDesk(squad.id)}
          rank={rank}
          hasEntryId={hasEntryId}
        />
      )}
    </>
  );
}

/** No squad loaded — every layout above needs one, so this replaces all
 * three rather than any of them rendering an empty shell. */
function NoSquadPanel() {
  return (
    <section className="panel" aria-label="No squad loaded">
      <h3>Load a squad to see this week's briefing</h3>
      <p className="hint">
        Import a screenshot or load a saved squad, and this becomes the one page that tells you
        what to do before the deadline.
      </p>
      <Link to="/squad" className="ghost small">
        Go to My Squad
      </Link>
    </section>
  );
}

interface PreDeadlineDeskProps {
  validation: SquadValidation | null;
  analysis: AnalysisResult | null;
  plan: TransferPlan | null;
  chipPlan: ChipPlan | null;
  problems: string[];
  planProblems: string[];
  chipPlanProblems: string[];
  running: boolean;
  onRunTheDesk: () => void;
  rank: RankContext | null;
  hasEntryId: boolean;
}

/** Before the deadline (or with the clock unknown) — the headline decision,
 * anything urgent about the squad, and the one primary action. Results from
 * a previous "Run the desk" (this session, or on `/squad`) stay on screen
 * exactly like `/squad` renders them, so this never contradicts what's
 * already known about the squad. */
function PreDeadlineDesk({
  validation,
  analysis,
  plan,
  chipPlan,
  problems,
  planProblems,
  chipPlanProblems,
  running,
  onRunTheDesk,
  rank,
  hasEntryId,
}: PreDeadlineDeskProps) {
  const urgentPlayers = analysis
    ? analysis.assessments.filter((a) => a.flags.some((flag) => URGENT_FLAGS.has(flag)))
    : [];
  const illegal = validation !== null && !validation.valid;

  // One note per failed leg of "Run the desk" — a leg that succeeded (or
  // was never run) contributes nothing here, so this stays empty until
  // something has actually gone wrong.
  const notes = [
    problems.length > 0 ? `The analysis didn't finish: ${problems[0]}` : null,
    planProblems.length > 0 ? `The transfer plan didn't finish: ${planProblems[0]}` : null,
    chipPlanProblems.length > 0 ? `The chip plan didn't finish: ${chipPlanProblems[0]}` : null,
  ].filter((note): note is string => note !== null);

  return (
    <>
      <section className="panel panel--accent" aria-label="This week's headline decision">
        <h3>{analysis ? "This week's headline decision is below" : "Run the desk to get this week's headline decision"}</h3>
        <p className="hint">
          Captain, transfers and chip, argued together for the squad you've got loaded.
        </p>
        {rank ? <Standing rank={rank} showMovement={false} /> : !hasEntryId && <NoStandingHint />}
        <button type="button" onClick={onRunTheDesk} disabled={running} aria-busy={running}>
          {running && <span className="spinner" aria-hidden="true" />}
          {running ? 'Running the desk…' : 'Run the desk'}
        </button>

        {notes.length > 0 && (
          <ul className="notices mt-md">
            {notes.map((note) => (
              <li key={note} className="error">
                {note}
              </li>
            ))}
          </ul>
        )}
      </section>

      {(illegal || urgentPlayers.length > 0) && (
        <section className="panel panel--bad" aria-label="Needs attention before the deadline">
          <h3>Before you do anything else</h3>
          {illegal && validation && (
            <>
              <p className="hint">This squad isn't legal — fix it before acting on any advice.</p>
              <ul className="notices">
                {validation.violations.map((violation) => (
                  <li key={violation.message} className="error">
                    {violation.message}
                  </li>
                ))}
              </ul>
            </>
          )}
          {urgentPlayers.length > 0 && (
            <ul className="notices">
              {urgentPlayers.map((player) => (
                <li key={player.name} className="error">
                  {player.name} — {player.flags.join(', ')}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {analysis && <AnalysisView analysis={analysis} />}
      {plan && <TransferPlanView plan={plan} />}
      {chipPlan && <ChipCalendar plan={chipPlan} />}
    </>
  );
}

interface LiveDeskProps {
  gameweek: number;
  points: number | null;
  rank: RankContext | null;
  hasEntryId: boolean;
}

/** Matches are live — nothing left to decide, just what's happened so far.
 * `/tracker` is where the rival-by-rival detail behind this figure lives. */
function LiveDesk({ gameweek, points, rank, hasEntryId }: LiveDeskProps) {
  return (
    <section className="panel panel--accent" aria-label="This gameweek's points so far">
      <h3>Gameweek {gameweek} is live</h3>
      <div className="stats">
        <div className="callout">
          <dt>Points so far</dt>
          <dd>
            <strong>{points ?? '—'}</strong>
          </dd>
        </div>
      </div>
      {rank ? <Standing rank={rank} showMovement={false} /> : !hasEntryId && <NoStandingHint />}
      <Link to="/tracker" className="ghost small">
        See how your rivals are doing
      </Link>
    </section>
  );
}

interface PostDeskProps {
  gameweek: number;
  points: number | null;
  rank: RankContext | null;
  hasEntryId: boolean;
}

/** The gameweek is done — the week's points, a link to the advice record,
 * and (via `DeadlineCountdown` above) the countdown to the next one. Shows
 * the standing's *movement*, not just its position — this is the one layout
 * where a mini-league position reflects a result that actually just
 * happened, not one still in progress. */
function PostDesk({ gameweek, points, rank, hasEntryId }: PostDeskProps) {
  return (
    <section className="panel panel--accent" aria-label="This gameweek's final points">
      <h3>Gameweek {gameweek} is done</h3>
      <div className="stats">
        <div className="callout">
          <dt>Points</dt>
          <dd>
            <strong>{points ?? '—'}</strong>
          </dd>
        </div>
      </div>
      {rank ? <Standing rank={rank} showMovement={true} /> : !hasEntryId && <NoStandingHint />}
      <Link to="/record" className="ghost small">
        See it in your advice record
      </Link>
    </section>
  );
}

/** Ordinal suffix for a rank or league position — "1st", "2nd", "3rd",
 * "11th", the usual English exceptions in the teens. */
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** A mini-league position's movement since the previous gameweek — same
 * arithmetic as `LeagueStandings`' own `movement` helper (a lower rank
 * number is an improvement), read straight off `RankContext`'s own
 * `previousRank` rather than a standings-page fetch. */
function leagueMovementLabel(rank: number, previousRank: number | null): string {
  if (previousRank === null) return 'not ranked last gameweek';
  const delta = previousRank - rank;
  if (delta === 0) return 'unchanged';
  return delta > 0 ? `up ${delta}` : `down ${Math.abs(delta)}`;
}

interface StandingProps {
  rank: RankContext;
  /** Only the post-gameweek layout shows movement — a mini-league position
   * mid-week hasn't finished moving yet, so stating it as a plain figure
   * there avoids implying a result that isn't in yet. */
  showMovement: boolean;
}

/** The premise the advice is arguing from, read as a sentence rather than a
 * stat card (see this ticket's own notes) — overall rank, where that sits as
 * a percentage of the field, and each mini-league position. */
function Standing({ rank, showMovement }: StandingProps) {
  return (
    <p className="standing">
      {rank.overallRank !== null ? (
        <>
          You're <strong>{ordinal(rank.overallRank)}</strong> overall
          {rank.percentile !== null && <> — the top {rank.percentile}% of the field</>}
          {rank.gameweeksRemaining > 0 && (
            <>
              , with {rank.gameweeksRemaining} gameweek{rank.gameweeksRemaining === 1 ? '' : 's'} left
            </>
          )}
          .
        </>
      ) : (
        "Your overall rank isn't known yet."
      )}
      {rank.leagues.length > 0 && (
        <>
          {' '}
          {rank.leagues.map((league: RankLeagueStanding, index: number) => (
            <span key={league.id}>
              {index === 0 ? 'In ' : ', '}
              {league.name} you're {league.rank !== null ? ordinal(league.rank) : 'unranked'}
              {showMovement && league.rank !== null && (
                <> ({leagueMovementLabel(league.rank, league.previousRank)})</>
              )}
            </span>
          ))}
          .
        </>
      )}
    </p>
  );
}

/** A squad with no FPL entry id has no rank to read at all — not a blank
 * space where the standing would be, and not a broken control; see
 * `SquadView`'s matching line for the objective control's own "auto" option. */
function NoStandingHint() {
  return <p className="hint">Import your squad with an FPL team id to see where you actually stand.</p>;
}
